import { useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled from 'styled-components/macro'

export function OverflownTextTooltip({
  children,
  maxLines = 1,
  tooltipText,
  placement = 'top',
  ...props
}: {
  children: React.ReactNode
  maxLines?: number
  tooltipText: string
  placement?: 'top' | 'right' | 'bottom' | 'left'
} & React.HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null)
  const [isOverflown, setIsOverflown] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const checkOverflow = () => {
      const computedStyle = getComputedStyle(element)
      const lineHeight = parseFloat(computedStyle.lineHeight)

      // Check if we have child elements (like FbLink) that might be handling overflow
      const checkElement =
        element.children.length > 0 ? element.children[0] : element

      const heightOverflow = element.scrollHeight > lineHeight * maxLines
      const widthOverflow =
        (checkElement as HTMLElement).scrollWidth >
        (checkElement as HTMLElement).clientWidth
      const isOverflowing = maxLines === 1 ? widthOverflow : heightOverflow

      setIsOverflown(isOverflowing)
    }

    checkOverflow()

    const resizeObserver = new ResizeObserver(checkOverflow)
    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
    }
  }, [maxLines, tooltipText])

  const tooltip = (
    // Tag it as position fixed !important to override the tooltip's default position: absolute
    <TooltipFixed>{tooltipText}</TooltipFixed>
  )

  return (
    <Wrapper>
      <OverlayTrigger
        placement={placement}
        overlay={isOverflown ? tooltip : <></>}
      >
        <TruncatedText ref={ref} {...props} maxLines={maxLines}>
          {children}
        </TruncatedText>
      </OverlayTrigger>
    </Wrapper>
  )
}

const TooltipFixed = styled(Tooltip)`
  position: fixed !important;
`

const TruncatedText = styled.span<{
  maxLines?: number
}>`
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;

  ${({ maxLines }) =>
    maxLines &&
    maxLines > 1 &&
    `
    display: -webkit-box;
    -webkit-line-clamp: ${maxLines};
    -webkit-box-orient: vertical;
    white-space: normal;
  `}
`

const Wrapper = styled.div`
  min-height: 100%;
  display: flex;
  align-items: center;
  z-index: 1;
  width: 100%;
  max-width: 100%;
  min-width: 0; /* Important for flex children to allow shrinking */
`
